import React, { useEffect, useState } from 'react';
import {
    Box, Grid, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Table, TableHead, TableBody, TableRow, TableCell, Radio, RadioGroup, Checkbox, Switch, IconButton, Chip, Paper, Tooltip,
} from '@mui/material';
import ButtonContained from '../../../Common/ButtonContained';
import CommonDropDown from '../../../Common/CommonDropDown';
import CommonTextField from '../../../Common/CommonTextField';
import { createFields, deleteFields, getDataById, updateFields } from '../../../../Redux/Actions/action';
import { showLoader } from '../../../../Redux/Slices/loaderSlice';
import ErrorHandler from '../../../../Utils/handler';
import { useDispatch } from 'react-redux';
import { addMemberEnum } from '../../../../Utils/enum';
import { toast } from 'react-toastify';
import DeleteIcon from '@mui/icons-material/Delete';
import TextLabel from '../../../Common/CommonTextLabel';
import { useConfirm } from 'material-ui-confirm';
import CommonTable from '../../../Common/CommonTable';
import StyledTableCell from '../../../Common/styledTableCell';
import CustomPagination from '../../../Common/CommonPagination';
import EditIcon from '@mui/icons-material/Edit';
import { UpdateSequenceComponent } from '../../../Common/UpdateSequenceComponent';
import AddIcon from '@mui/icons-material/Add';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle';
import NumbersIcon from '@mui/icons-material/Numbers';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import ViewModuleIcon from '@mui/icons-material/ViewModule';

// Options for the dropdown with icons
const options = [
    { label: "Personal Details", value: addMemberEnum.personalDetails, icon: "👤" },
    { label: "Id Proof Details", value: addMemberEnum.idProofDetails, icon: "📄" },
    { label: "Address Details", value: addMemberEnum.addressDetails, icon: "🏠" },
    { label: "Address Proof Details", value: addMemberEnum.addressProofDetails, icon: "📍" },
    { label: "Village Details", value: addMemberEnum.villageDetails, icon: "🏘️" },
    { label: "Other Details", value: addMemberEnum.otherDetails, icon: "📋" },
    { label: "Nominee Details", value: addMemberEnum.nomineeDetails, icon: "👨‍👩‍👧" },
    { label: "Additional Details", value: addMemberEnum.additionalDetails, icon: "📎" },
];

// Action types with icons and colors
export const actionEnum = [
    { label: "Text", value: "text", icon: <TextFieldsIcon fontSize="small" />, color: "#2196f3" },
    { label: "Dropdown", value: "dropdown", icon: <ArrowDropDownCircleIcon fontSize="small" />, color: "#9c27b0" },
    { label: "Number", value: "number", icon: <NumbersIcon fontSize="small" />, color: "#ff9800" },
    { label: "Date", value: "date", icon: <CalendarMonthIcon fontSize="small" />, color: "#4caf50" },
    { label: "Tab", value: "tab", icon: <ViewModuleIcon fontSize="small" />, color: "#607d8b" },
    { label: "File", value: "file", icon: <AttachFileIcon fontSize="small" />, color: "#e91e63" },
    { label: "Radio", value: "radio", icon: <RadioButtonCheckedIcon fontSize="small" />, color: "#00bcd4" },
];

// Get field type chip color
const getFieldTypeChip = (type) => {
    const actionType = actionEnum.find(a => a.value === type);
    return actionType || { label: type, color: "#757575", icon: <TextFieldsIcon fontSize="small" /> };
};

function capitalizeFirstLetter(value) {
    if (typeof value !== 'string') return '';
    return value.charAt(0).toUpperCase() + value.slice(1);
}

const headerList = ["Sequence", "Field Name", "Define Action", "IsMandatory", "IsActive", "Edit", "Delete"]

const GeneralSection = () => {
    // ---------- Hook -----------
    const dispatch = useDispatch();
    const confirmDialogue = useConfirm();

    // --------------- State -------------
    const [selectedSection, setSelectedSection] = useState({});
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newFieldValue, setNewFieldValue] = useState("");
    const [fields, setFields] = useState([
        {
            "name": "",
            "key": "",
            "type": "Text",
            "isActive": false,
            "isMandatory": false,
        }
    ]);
    const [fieldData, setFieldData] = useState([]);
    const [fieldUpdateDataId, setFieldUpdateDataId] = useState();
    const [error, setError] = useState();
    const [page, setPage] = useState(1);
    const [isEdit, setIsEdit] = useState(false);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [isSequence, setIsSequence] = React.useState({ indexNo: "", open: false })


    // --------------- handleChange ---------------
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(1);
    };

    const detailsHandler = (e) => {
        const value = e.target.value;
        const selected = options?.length > 0 && options?.find((e) => e.label == value)
        setSelectedSection(selected);
    };

    const handleOpenModal = () => {
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsEdit(false)
        setError('')
        setIsModalOpen(false);
        setNewFieldValue(""); // Clear the input when the modal is closed 
        setFields([{
            "name": "",
            "key": "",
            "type": "Text",
            "isActive": false,
            "isMandatory": false
        }]);
    };

    const convertToCaseSensitive = (value) => {
        if (typeof value !== 'string') return '';
        return value.toLowerCase().replace(/\s+(\w)/g, (_, letter) => letter.toUpperCase());
    };

    const validateSequence = (previousSequence) => {
        if (fields?.indexNo !== previousSequence) {
            return true
        } else {
            toast.error("Sequence you entered is same !");
            return false
        }
    }

    // -------------- API calling -------------
    const _getData = async () => {
        dispatch(showLoader(true))
        const body = {
            sectionIds: [selectedSection?.value?.toString()]
        }
        try {
            const result = await dispatch(getDataById(body))
            const error = ErrorHandler(result)
            if (error) {
                setFieldData(result?.payload?.data?.sections?.[0]?.fields)
                setFieldUpdateDataId(result?.payload?.data?.sections?.[0])
            }
        } catch (error) {
        } finally {
            dispatch(showLoader(false))
        }
    }

    // add Filed
    const _createFields = async () => {
        if (!fields?.[0]?.name) {
            setError({ fieldsName: "Please enter field name" });
            return;
        }
        const actionType = actionEnum?.find((val) => val?.label === fields?.[0]?.type)?.value
        dispatch(showLoader(true))
        const body = {
            "sectionId": selectedSection?.value?.toString(),
            "fieldKey": convertToCaseSensitive(fields?.[0]?.name),
            "fieldName": fields?.[0]?.name,
            "defineActionType": actionType,
            "isActive": fields?.[0]?.isActive,
            "isMandatory": fields?.[0]?.isMandatory,
        }
        if (isEdit) {
            // body._id = fields?.[0]?.id
            body.fieldKey = fields?.[0]?.key
        }
        try {
            const fn = isEdit ? updateFields : createFields
            const res = await dispatch(fn(body));
            const error = ErrorHandler(res)
            if (error) {
                toast.success(res?.payload?.message);
                setIsModalOpen(false);
                setIsEdit(false);
                setNewFieldValue("");
                setFields([{
                    "name": "",
                    "key": "",
                    "type": "Text",
                    "isActive": false,
                    "isMandatory": false
                }]);
                _getData();
            }
        } catch (error) {
        } finally {
            dispatch(showLoader(false))
        }
    }

    const _updateSequence = async (field) => {
        if (!validateSequence(isSequence?.indexNo)) {
            return;
        }
        dispatch(showLoader(true))
        const body = {
            // "_id": field?._id,
            "sectionId": selectedSection?.value?.toString(),
            "fieldKey": field?.fieldKey,
            "fieldName": field?.fieldName,
            "defineActionType": field?.defineActionType,
            "isActive": field?.isActive,
            "isMandatory": field?.isMandatory,
            "indexNo": +isSequence?.indexNo,
        }
        try {
            const result = await dispatch(updateFields(body))
            const error = ErrorHandler(result)
            if (error) {
                toast.success(result?.payload?.message);
                _getData()
            }
        } catch (error) {
        } finally {
            dispatch(showLoader(false))
        }
    }

    // update isMandatory & isActive
    const active_inactive = async (data, check, isBlock) => {
        dispatch(showLoader(true))
        const body = {
            // "_id": fieldUpdateDataId?._id,
            "fieldKey": data?.fieldKey,
            "fieldName": data?.fieldName,
            "sectionId": fieldUpdateDataId?.sectionId,
            "defineActionType": data?.defineActionType,
            "isMandatory": check === "isMandatory" ? isBlock : data?.isMandatory,
            "isActive": check === "isActive" ? isBlock : data?.isActive,
        }
        try {
            const res = await dispatch(updateFields(body));
            const error = ErrorHandler(res)
            if (error) {
                toast.success(res?.payload?.message);
                _getData()
            }
        } catch (error) {
        } finally {
            dispatch(showLoader(false))
        }
    }

    // delete
    const _deleteFiled = async (fieldKey) => {
        dispatch(showLoader(true));
        let body = {
            "fieldKey": fieldKey,
            "sectionId": fieldUpdateDataId?.sectionId,
        }
        try {
            const result = await dispatch(deleteFields(body));
            const error = ErrorHandler(result);
            if (error) {
                toast?.success(result?.payload?.message)
                _getData()
            }
        } catch (error) {
        } finally {
            dispatch(showLoader(false));
        }
    }

    // ---------------- useEffect ----------------
    useEffect(() => {
        if (selectedSection?.value) {
            _getData()
        }
    }, [selectedSection?.value, page, rowsPerPage]);

    useEffect(() => {
        if (options.length > 0) {
            setSelectedSection(options[0]);
        }
    }, []);

    return (
        <>
            <Grid container spacing={3}>
                {/* Header Section */}
                <Grid item xs={12}>
                    <Paper 
                        elevation={0} 
                        sx={{ 
                            p: 3, 
                            borderRadius: "16px", 
                            background: "linear-gradient(135deg, #3E4094 0%, #5a5cb8 100%)",
                            color: "#fff"
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                            <Box>
                                <Typography sx={{ fontSize: '24px', fontWeight: 600, mb: 0.5 }}>
                                    Registration Form Fields
                                </Typography>
                                <Typography sx={{ fontSize: '14px', opacity: 0.9 }}>
                                    Manage dynamic form fields for Admin & Mobile App registration
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                                <Box sx={{ minWidth: 200, bgcolor: "rgba(255,255,255,0.15)", borderRadius: "12px" }}>
                                    <CommonDropDown
                                        width="100%"
                                        values={options.map((option) => option.label)}
                                        onChange={(e) => detailsHandler(e)}
                                        name="Details"
                                        defaultValue={"Easy"}
                                        value={selectedSection?.label}
                                        valid
                                        notShowSelect
                                    />
                                </Box>
                                <ButtonContained
                                    height={'42px'}
                                    text={"Add New Field"}
                                    startIcon={<AddIcon />}
                                    onClick={handleOpenModal}
                                    sx={{ 
                                        bgcolor: "#fff", 
                                        color: "#3E4094",
                                        "&:hover": { bgcolor: "#f5f5f5" }
                                    }}
                                />
                            </Box>
                        </Box>
                    </Paper>
                </Grid>

                {/* Section Info Card */}
                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                        {options.map((opt) => (
                            <Chip
                                key={opt.value}
                                label={`${opt.icon} ${opt.label}`}
                                onClick={() => setSelectedSection(opt)}
                                variant={selectedSection?.value === opt.value ? "filled" : "outlined"}
                                sx={{
                                    cursor: 'pointer',
                                    fontWeight: selectedSection?.value === opt.value ? 600 : 400,
                                    bgcolor: selectedSection?.value === opt.value ? "#3E4094" : "transparent",
                                    color: selectedSection?.value === opt.value ? "#fff" : "#666",
                                    borderColor: selectedSection?.value === opt.value ? "#3E4094" : "#ddd",
                                    "&:hover": {
                                        bgcolor: selectedSection?.value === opt.value ? "#3E4094" : "#f5f5f5",
                                    }
                                }}
                            />
                        ))}
                    </Box>
                </Grid>

                {/* Fields Table */}
                <Grid item xs={12}>
                    <Paper elevation={0} sx={{ borderRadius: "16px", border: "1px solid #eee", overflow: "hidden" }}>
                        <Box sx={{ p: 2, borderBottom: "1px solid #eee", bgcolor: "#f8f9fa" }}>
                            <Typography fontWeight={600} color="#3E4094">
                                {selectedSection?.icon} {selectedSection?.label} - {fieldData?.length || 0} Fields
                            </Typography>
                        </Box>
                        <CommonTable
                            headerList={headerList}
                            minWidth={600}
                        >
                            {fieldData?.length > 0 ? fieldData?.map((field, i) => (
                                <TableRow 
                                    key={i}
                                    sx={{ 
                                        "&:hover": { bgcolor: "#f8f9fa" },
                                        transition: "background 0.2s"
                                    }}
                                >
                                    <StyledTableCell align='center'>
                                        <UpdateSequenceComponent
                                            isSequence={isSequence}
                                            setIsSequence={setIsSequence}
                                            index={i}
                                            value={field}
                                            onClick={() => {
                                                if (validateSequence(field?.indexNo)) {
                                                    _updateSequence(field)
                                                    setIsSequence({ open: false })
                                                }
                                            }}
                                        />
                                    </StyledTableCell>
                                    <StyledTableCell align='left'>
                                        <Typography fontWeight={500}>{field?.fieldName}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Key: {field?.fieldKey}
                                        </Typography>
                                    </StyledTableCell>
                                    <StyledTableCell align='center'>
                                        <Chip
                                            icon={getFieldTypeChip(field?.defineActionType)?.icon}
                                            label={capitalizeFirstLetter(field?.defineActionType)}
                                            size="small"
                                            sx={{
                                                bgcolor: `${getFieldTypeChip(field?.defineActionType)?.color}15`,
                                                color: getFieldTypeChip(field?.defineActionType)?.color,
                                                fontWeight: 500,
                                                "& .MuiChip-icon": { color: getFieldTypeChip(field?.defineActionType)?.color }
                                            }}
                                        />
                                    </StyledTableCell>
                                    <StyledTableCell align='center'>
                                        <Tooltip title={field?.isMandatory ? "Required field" : "Optional field"}>
                                            <Switch
                                                checked={field?.isMandatory}
                                                color="warning"
                                                size="small"
                                                onChange={(e) => {
                                                    active_inactive(field, "isMandatory", e.target.checked);
                                                }}
                                            />
                                        </Tooltip>
                                    </StyledTableCell>
                                    <StyledTableCell align='center'>
                                        <Tooltip title={field?.isActive ? "Field visible" : "Field hidden"}>
                                            <Switch
                                                checked={field?.isActive}
                                                color="success"
                                                size="small"
                                                onChange={(e) => {
                                                    active_inactive(field, "isActive", e.target.checked);
                                                }}
                                            />
                                        </Tooltip>
                                    </StyledTableCell>
                                    <StyledTableCell align='center'>
                                        <Tooltip title="Edit field">
                                            <IconButton 
                                                size="small"
                                                sx={{ 
                                                    bgcolor: "#e3f2fd", 
                                                    "&:hover": { bgcolor: "#bbdefb" }
                                                }}
                                                onClick={() => {
                                                    setIsEdit(true); 
                                                    setIsModalOpen(true); 
                                                    setFields([{
                                                        "name": field?.fieldName,
                                                        "key": field?.fieldKey,
                                                        "type": capitalizeFirstLetter(field?.defineActionType),
                                                        "isActive": field?.isActive,
                                                        "isMandatory": field?.isMandatory,
                                                    }]);
                                                }}
                                            >
                                                <EditIcon fontSize="small" sx={{ color: "#1976d2" }} />
                                            </IconButton>
                                        </Tooltip>
                                    </StyledTableCell>
                                    <StyledTableCell align='center'>
                                        <Tooltip title="Delete field">
                                            <IconButton 
                                                size="small"
                                                sx={{ 
                                                    bgcolor: "#ffebee", 
                                                    "&:hover": { bgcolor: "#ffcdd2" }
                                                }}
                                                onClick={() =>
                                                    confirmDialogue({
                                                        title: `Delete "${field?.fieldName}"?`,
                                                        description: "This will remove the field from both Admin and Mobile App forms.",
                                                    })
                                                        .then(() => _deleteFiled(field?.fieldKey))
                                                        .catch(() => {})
                                                }
                                            >
                                                <DeleteIcon fontSize="small" sx={{ color: "#d32f2f" }} />
                                            </IconButton>
                                        </Tooltip>
                                    </StyledTableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <StyledTableCell colSpan={7} align="center">
                                        <Box py={4}>
                                            <Typography color="text.secondary">No fields added yet</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Click "Add New Field" to create your first field
                                            </Typography>
                                        </Box>
                                    </StyledTableCell>
                                </TableRow>
                            )}
                        </CommonTable>
                    </Paper>
                    
                    {fieldData?.count?.length > 0 && (
                        <Grid item xs={12} sx={{ mt: 2 }}>
                            <CustomPagination
                                rowsPerPageOptions={[5, 10, 25, 50]}
                                count={Math.ceil(fieldData?.count / rowsPerPage)}
                                page={page}
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </Grid>
                    )}
                </Grid>
            </Grid>

            {/* Modal with Improved UI */}
            <Dialog 
                open={isModalOpen} 
                onClose={handleCloseModal} 
                maxWidth="md" 
                fullWidth
                PaperProps={{
                    sx: { borderRadius: "16px" }
                }}
            >
                <DialogTitle sx={{ 
                    borderBottom: "1px solid #eee", 
                    pb: 2,
                    background: "linear-gradient(135deg, #3E4094 0%, #5a5cb8 100%)",
                    color: "#fff"
                }}>
                    <Box display="flex" alignItems="center" gap={1}>
                        {isEdit ? <EditIcon /> : <AddIcon />}
                        <Typography variant="h6" fontWeight={600}>
                            {isEdit ? 'Edit Field' : 'Add New Field'}
                        </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                        {selectedSection?.icon} Adding to: {selectedSection?.label}
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
                        {fields?.map((field, index) => (
                            <Box key={field?.id || field?.fieldKey || index}>
                                {/* Field Name Input */}
                                <Box sx={{ mb: 3 }}>
                                    <Typography fontWeight={500} mb={1} color="#333">
                                        Field Name <Typography component="span" color="error">*</Typography>
                                    </Typography>
                                    <CommonTextField
                                        value={field?.name}
                                        placeholder="Enter field name (e.g., Full Name, Phone Number)"
                                        onChange={(e) =>
                                            setFields((prevFields) =>
                                                prevFields.map((f, i) =>
                                                    i === index ? { ...f, name: e.target.value } : f
                                                )
                                            )
                                        }
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                    />
                                    <TextLabel color='error' title={!field?.name ? error?.fieldsName : ""} />
                                </Box>

                                {/* Field Type Selection */}
                                <Box sx={{ mb: 3 }}>
                                    <Typography fontWeight={500} mb={1.5} color="#333">
                                        Field Type
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                        {actionEnum?.map((val) => (
                                            <Paper
                                                key={val.value}
                                                elevation={0}
                                                onClick={() =>
                                                    setFields((prevFields) =>
                                                        prevFields.map((f, i) =>
                                                            i === index ? { ...f, type: val.label } : f
                                                        )
                                                    )
                                                }
                                                sx={{
                                                    p: 1.5,
                                                    px: 2,
                                                    cursor: 'pointer',
                                                    border: field?.type === val.label ? `2px solid ${val.color}` : '2px solid #eee',
                                                    borderRadius: '12px',
                                                    bgcolor: field?.type === val.label ? `${val.color}10` : '#fff',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    transition: 'all 0.2s',
                                                    '&:hover': {
                                                        borderColor: val.color,
                                                        bgcolor: `${val.color}05`,
                                                    }
                                                }}
                                            >
                                                <Box sx={{ color: val.color }}>{val.icon}</Box>
                                                <Typography 
                                                    variant="body2" 
                                                    fontWeight={field?.type === val.label ? 600 : 400}
                                                    color={field?.type === val.label ? val.color : '#666'}
                                                >
                                                    {val.label}
                                                </Typography>
                                            </Paper>
                                        ))}
                                    </Box>
                                </Box>

                                {/* Status Options */}
                                <Box>
                                    <Typography fontWeight={500} mb={1.5} color="#333">
                                        Field Options
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 3 }}>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 2,
                                                flex: 1,
                                                border: field?.isMandatory ? '2px solid #ff9800' : '2px solid #eee',
                                                borderRadius: '12px',
                                                bgcolor: field?.isMandatory ? '#fff8e1' : '#fff',
                                            }}
                                        >
                                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                                <Box>
                                                    <Typography fontWeight={500}>Required Field</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        User must fill this field
                                                    </Typography>
                                                </Box>
                                                <Switch
                                                    checked={field?.isMandatory}
                                                    color="warning"
                                                    onChange={(e) =>
                                                        setFields((prevFields) =>
                                                            prevFields.map((f, i) =>
                                                                i === index ? { ...f, isMandatory: e.target.checked } : f
                                                            )
                                                        )
                                                    }
                                                />
                                            </Box>
                                        </Paper>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 2,
                                                flex: 1,
                                                border: '2px solid #eee',
                                                borderRadius: '12px',
                                                bgcolor: '#f5f5f5',
                                                opacity: 0.7
                                            }}
                                        >
                                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                                <Box>
                                                    <Typography fontWeight={500}>Active Status</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Managed from table
                                                    </Typography>
                                                </Box>
                                                <Switch
                                                    checked={field?.isActive}
                                                    disabled
                                                    color="success"
                                                />
                                            </Box>
                                        </Paper>
                                    </Box>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: "1px solid #eee", gap: 1 }}>
                    <ButtonContained 
                        onClick={handleCloseModal} 
                        text="Cancel" 
                        sx={{ bgcolor: "#f5f5f5", color: "#666", "&:hover": { bgcolor: "#eee" } }}
                    />
                    <ButtonContained 
                        text={isEdit ? "Update Field" : "Create Field"} 
                        onClick={_createFields}
                        startIcon={isEdit ? <EditIcon /> : <AddIcon />}
                    />
                </DialogActions>
            </Dialog>
        </>
    );
};

export default GeneralSection;

